/**
 * Admin dashboard update detection (aligned with frontend PWA update flow).
 * Polls /version.json + listens for service worker updates after Hosting deploy.
 */
export const ADMIN_UPDATE_EVENT = 'admin-update-available';

const FLAG_KEY = 'admin_update_available';
const DISMISS_KEY = 'admin_update_dismissed_version';
const SNOOZE_UNTIL_KEY = 'admin_update_snooze_until';
const LAST_CHECK_KEY = 'admin_update_last_check';

const MIN_CHECK_INTERVAL_MS = 15 * 1000;
const POLL_INTERVAL_MS = 20 * 1000;
const SNOOZE_MS = 60 * 1000;
const BOOT_BUILD_KEY = 'admin_boot_build_id';

let detectionStarted = false;

function isLocalDevHost() {
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1' || h.includes('localhost');
}

export function isAdminUpdateAvailableFlag() {
  try {
    return localStorage.getItem(FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

function wasSnoozedFor(version) {
  try {
    const dismissed = sessionStorage.getItem(DISMISS_KEY);
    if (dismissed !== String(version || 'pending')) return false;
    const until = parseInt(sessionStorage.getItem(SNOOZE_UNTIL_KEY) || '0', 10);
    return Number.isFinite(until) && Date.now() < until;
  } catch {
    return false;
  }
}

export function dismissAdminUpdate(version) {
  try {
    sessionStorage.setItem(DISMISS_KEY, String(version || 'pending'));
    sessionStorage.setItem(SNOOZE_UNTIL_KEY, String(Date.now() + SNOOZE_MS));
  } catch {
    /* ignore */
  }
}

export function notifyAdminUpdateAvailable(detail = {}) {
  if (typeof window === 'undefined') return;
  const version = detail.version || 'pending';
  if (wasSnoozedFor(version)) return;

  try {
    localStorage.setItem(FLAG_KEY, '1');
  } catch {
    /* ignore */
  }

  window.dispatchEvent(
    new CustomEvent(ADMIN_UPDATE_EVENT, {
      detail: {
        source: detail.source || 'unknown',
        version,
        ...detail,
      },
    })
  );
}

function getLocalBuildId() {
  try {
    const meta = document.querySelector('meta[name="kairos-build-id"]')?.getAttribute('content');
    if (meta) return meta;
  } catch {
    /* ignore */
  }
  try {
    return sessionStorage.getItem(BOOT_BUILD_KEY);
  } catch {
    return null;
  }
}

function rememberBootBuildId() {
  try {
    const meta = document.querySelector('meta[name="kairos-build-id"]')?.getAttribute('content');
    if (meta) sessionStorage.setItem(BOOT_BUILD_KEY, meta);
  } catch {
    /* ignore */
  }
}

async function fetchRemoteBuildId() {
  const url = `/version.json?_=${Date.now()}&r=${Math.random().toString(36).slice(2, 8)}`;
  const response = await fetch(url, {
    method: 'GET',
    cache: 'no-store',
    headers: {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      Pragma: 'no-cache',
    },
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const data = await response.json();
  if (!data?.buildId) throw new Error('No buildId');
  return data.buildId;
}

async function checkVersionJsonUpdate(force = false) {
  try {
    if (!force) {
      const last = parseInt(localStorage.getItem(LAST_CHECK_KEY) || '0', 10);
      if (Date.now() - last < MIN_CHECK_INTERVAL_MS) return;
    }
    localStorage.setItem(LAST_CHECK_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }

  let remoteBuildId;
  try {
    remoteBuildId = await fetchRemoteBuildId();
  } catch {
    return;
  }

  const localBuildId = getLocalBuildId();
  const remoteVersion = `build:${remoteBuildId}`;

  if (!localBuildId) return;

  if (remoteBuildId !== localBuildId) {
    notifyAdminUpdateAvailable({
      source: 'version-json',
      version: remoteVersion,
      currentVersion: `build:${localBuildId}`,
    });
  } else {
    try {
      localStorage.removeItem(FLAG_KEY);
    } catch {
      /* ignore */
    }
  }
}

function bindServiceWorkerUpdates(registration) {
  const onUpdateFound = () => {
    const worker = registration.installing;
    if (!worker) return;
    worker.addEventListener('statechange', () => {
      if (worker.state === 'installed' && navigator.serviceWorker.controller) {
        notifyAdminUpdateAvailable({ source: 'service-worker', version: 'pending' });
        checkVersionJsonUpdate(true).catch(() => {});
      }
    });
  };

  registration.addEventListener('updatefound', onUpdateFound);

  if (registration.waiting && navigator.serviceWorker.controller) {
    notifyAdminUpdateAvailable({ source: 'service-worker-waiting', version: 'pending' });
  }

  return () => {
    registration.removeEventListener('updatefound', onUpdateFound);
  };
}

export function checkAdminUpdateNow(force = true) {
  if (typeof window === 'undefined') return Promise.resolve();
  if (isLocalDevHost() || !import.meta.env.PROD) return Promise.resolve();
  return checkVersionJsonUpdate(force).catch(() => {});
}

export async function applyAdminUpdate() {
  try {
    localStorage.removeItem(FLAG_KEY);
    sessionStorage.removeItem(DISMISS_KEY);
    sessionStorage.removeItem(SNOOZE_UNTIL_KEY);
  } catch {
    /* ignore */
  }

  try {
    if (typeof window.__adminApplySWUpdate === 'function') {
      await window.__adminApplySWUpdate();
    }
  } catch {
    /* ignore */
  }

  try {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.getRegistration();
      registration?.waiting?.postMessage({ type: 'SKIP_WAITING' });
    }
  } catch {
    /* ignore */
  }

  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(
        keys
          .filter((k) => /admin|workbox|html/i.test(k))
          .map((k) => caches.delete(k))
      );
    }
  } catch {
    /* ignore */
  }

  const next = new URL(window.location.href);
  next.searchParams.set('_boot', String(Date.now()));
  window.location.replace(next.toString());
}

export function initAdminUpdateDetection() {
  if (typeof window === 'undefined') return;
  if (!import.meta.env.PROD || isLocalDevHost()) return;
  if (detectionStarted) return;
  detectionStarted = true;
  rememberBootBuildId();

  if (isAdminUpdateAvailableFlag()) {
    notifyAdminUpdateAvailable({ source: 'stored-flag' });
  }

  const boot = async () => {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          bindServiceWorkerUpdates(registration);
          registration.update().catch(() => {});
        }
      } catch {
        /* ignore */
      }
    }

    setTimeout(() => {
      checkVersionJsonUpdate(true).catch(() => {});
    }, 1200);
  };

  boot();

  const onFocus = () => checkAdminUpdateNow(true);
  const onVisibility = () => {
    if (!document.hidden) onFocus();
  };

  window.addEventListener('focus', onFocus);
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('online', onFocus);

  setInterval(() => {
    if (document.hidden) return;
    checkAdminUpdateNow(false);
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration().then((reg) => reg?.update?.()).catch(() => {});
    }
  }, POLL_INTERVAL_MS);
}
